'use client';

import * as React from 'react';
import { Tooltip } from '@ghq-abi/design-system-v2';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as PopoverPrimitive from '@radix-ui/react-popover';

import { Icon } from '~/shared/components/Icon';
import { cn } from '~/shared/utils/cn';

type WithClassName<T = unknown> = T & { className?: string };

const Root = (props: React.ComponentProps<typeof DialogPrimitive.Root>) => (
  <DialogPrimitive.Root modal={false} {...props} />
);

const { Trigger } = DialogPrimitive;

type BarProps = {
  children: React.ReactNode;
  className?: string;
  openMenu?: () => void;
  closeMenu?: () => void;
  label?: string;
};

const Bar = React.forwardRef<HTMLElement, BarProps>(
  ({ children, className, openMenu, closeMenu, label = 'Main', ...rest }, ref) => (
      <nav
        ref={ref}
        aria-label={label}
        data-state="collapsed"
        className={cn(
          'fixed left-0 top-[var(--app-banner-height,0px)] z-[1100] flex h-[calc(100vh-var(--app-banner-height,0px))] w-[72px] cursor-pointer flex-col justify-between border-0 border-r border-r-[#EEEFF2] bg-white p-0 font-[OpenSans] shadow-[0px_6px_7px_-2px_#00000014] focus:outline-none [&_ul]:overflow-hidden',
          className
        )}
        onMouseEnter={() => openMenu?.()}
        onMouseLeave={() => closeMenu?.()}
        onFocusCapture={() => openMenu?.()}
        {...rest}
      >
        <Tooltip.Provider delayDuration={200}>{children}</Tooltip.Provider>
      </nav>
    )
);
Bar.displayName = 'AppMenuBar';

type ContentProps = {
  children: React.ReactNode;
  className?: string;
  overlayClassName?: string;
  overlayIsActive?: boolean;
  closeMenu?: () => void;
  keepOpen?: () => void;
  ariaLabel?: string;
} & React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>;

const Content = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  ContentProps
>(
  (
    {
      children,
      className,
      overlayClassName,
      overlayIsActive,
      closeMenu,
      keepOpen,
      ariaLabel = 'Main',
      ...rest
    },
    ref
  ) => (
      <DialogPrimitive.Portal>
        {overlayIsActive && (
          <div
            className={cn(
              'fixed inset-0 z-[1300] bg-black/25 motion-safe:animate-app-menu-overlay-show',
              overlayClassName
            )}
            onClick={closeMenu}
            aria-hidden
          />
        )}
        <DialogPrimitive.Content
          ref={ref}
          aria-label={ariaLabel}
          data-state="expanded"
          {...rest}
          onOpenAutoFocus={event => event.preventDefault()}
          onMouseOver={() => keepOpen?.()}
          onMouseLeave={() => closeMenu?.()}
          onEscapeKeyDown={event => {
            event.preventDefault();
            closeMenu?.();
          }}
          onPointerDownOutside={() => closeMenu?.()}
          className={cn(
            'fixed left-0 top-[var(--app-banner-height,0px)] z-[1400] flex h-[calc(100vh-var(--app-banner-height,0px))] w-[220px] flex-col justify-between border-0 border-r border-r-[#EEEFF2] bg-white p-0 font-[OpenSans] no-underline shadow-[8px_0px_16px_0px_#191F2E3D] focus:outline-none motion-safe:data-[state=open]:animate-app-menu-content-show motion-safe:data-[state=closed]:animate-app-menu-content-hide [&_img]:h-auto [&_img]:w-full [&_img]:object-contain',
            className
          )}
        >
          {children}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    )
);
Content.displayName = 'AppMenuContent';

type HeaderProps = WithClassName<{ children?: React.ReactNode }>;

const Header = React.forwardRef<HTMLDivElement, HeaderProps>(
  ({ children, className }, ref) => (
    <div
      ref={ref}
      className={cn(
        'h-(--header-height) shrink-0 border-b border-b-[#EEEFF2]',
        className
      )}
    >
      {children}
    </div>
  )
);
Header.displayName = 'AppMenuHeader';

type FooterProps = WithClassName<{ children: React.ReactNode }>;

const Footer = React.forwardRef<HTMLDivElement, FooterProps>(
  ({ children, className }, ref) => (
    <div
      ref={ref}
      className={cn('shrink-0 border-t border-t-[#EEEFF2] p-2', className)}
    >
      {children}
    </div>
  )
);
Footer.displayName = 'AppMenuFooter';

type MenuIconButtonProps = WithClassName<{
  icon: React.ReactNode;
  onClick: () => void;
}>;

const MenuIconButton = React.forwardRef<HTMLButtonElement, MenuIconButtonProps>(
  ({ icon, onClick, className }, ref) => (
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center justify-center rounded-full border-none bg-transparent! p-1.5 outline-none hover:bg-[#ededef]!',
        className
      )}
    >
      {icon}
    </button>
  )
);
MenuIconButton.displayName = 'AppMenuIconButton';

type MenuListProps = {
  children: React.ReactNode;
  bottomElement?: React.ReactNode;
  isMainList?: boolean;
  className?: string;
  bottomElementClassName?: string;
};

const MenuList = React.forwardRef<HTMLDivElement, MenuListProps>(
  (
    { children, bottomElement, isMainList, className, bottomElementClassName },
    forwardedRef
  ) => {
    const bottomRef = React.useRef<HTMLDivElement>(null);

    const baseListClasses =
      'flex h-full w-full flex-col items-stretch justify-start gap-2 p-2 [&_a]:no-underline';

    if (isMainList) {
      return (
        <div className="relative flex min-h-0 flex-1 flex-col overflow-y-auto [&_ul>:last-child_li]:mb-1">
          <div
            ref={forwardedRef}
            className={cn('relative z-[1]', baseListClasses, className)}
          >
            {children}
          </div>
          {bottomElement && (
            <div
              ref={bottomRef}
              className={cn(
                'fixed bottom-[60px] w-[220px]',
                bottomElementClassName
              )}
            >
              {bottomElement}
            </div>
          )}
        </div>
      );
    }

    return (
      <div
        ref={forwardedRef}
        data-app-menu-list
        className={cn(baseListClasses, className)}
      >
        {children}
      </div>
    );
  }
);
MenuList.displayName = 'AppMenuList';

const menuItemBase =
  'flex h-12 w-full cursor-pointer select-none list-none items-center gap-4 overflow-visible rounded text-left text-sm font-normal leading-5 text-[#191F2E] bg-transparent font-[OpenSans] outline-none focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#191F2E] focus-visible:ring-offset-2 [&_span]:opacity-100 motion-safe:[&_span:not([data-notification-badge])]:animate-app-menu-text-fade-in';

const iconBox =
  'relative inline-flex h-10 w-10 shrink-0 items-center justify-center overflow-visible';

type MenuItemProps = {
  title?: string;
  route?: string;
  isActive?: boolean;
  leftIcon?: React.ReactNode;
  isTriggerItem?: boolean;
  isSubMenuItem?: boolean;
  hasSubmenu?: boolean;
  isSubMenuOpen?: boolean;
  renderSubmenu?: React.ReactNode;
  showNotificationBadge?: boolean;
  className?: string;
  buttonClassName?: string;
  as?: React.ElementType;
  onHoverDelay?: number;
  onClick?: () => void;
  onHover?: () => void;
  tooltip?: string;
};

const CollapsedLabel = ({
  label,
  children,
}: {
  label?: string;
  children: React.ReactNode;
}): React.ReactElement => {
  if (!label) return children as React.ReactElement;

  return (
    <Tooltip.Root>
      <Tooltip.Trigger asChild>{children}</Tooltip.Trigger>
      <Tooltip.Portal>
        <Tooltip.Content side="right" sideOffset={8} className="z-[1500]">
          {label}
        </Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip.Root>
  );
};

const NotificationBadge = () => (
  <span
    className="absolute -top-2 -right-2 z-[1] flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-[#FF3236] p-px text-[10px] text-white"
    aria-hidden
    data-notification-badge
  >
    1
  </span>
);

const MenuItem = React.forwardRef<HTMLElement, MenuItemProps>(
  (
    {
      title,
      route,
      isActive,
      leftIcon,
      hasSubmenu,
      isSubMenuOpen,
      renderSubmenu,
      isTriggerItem,
      isSubMenuItem,
      showNotificationBadge,
      className,
      buttonClassName,
      as: Component = 'a',
      onHoverDelay = 300,
      onClick,
      onHover,
      tooltip,
    },
    forwardedRef
  ) => {
    const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

    const hoverBg = isSubMenuItem
      ? 'hover:bg-[#ededef]!'
      : 'hover:bg-[#F2DC39]!';
    const activeBg = isActive ? 'bg-[#F2DC39]' : 'bg-transparent';
    const justify = isTriggerItem
      ? 'justify-center px-0'
      : 'justify-start px-2';

    const itemContent = (
      <div className="flex items-center justify-center gap-4">
        {(leftIcon || showNotificationBadge) && (
          <div className={iconBox}>
            {leftIcon}
            {showNotificationBadge && <NotificationBadge />}
          </div>
        )}
        {!isTriggerItem ? <span>{title}</span> : null}
      </div>
    );

    const tooltipLabel = isTriggerItem ? (tooltip ?? title) : undefined;

    if (hasSubmenu) {
      return (
        <PopoverPrimitive.Root open={isSubMenuOpen} modal={false}>
          <div className={className}>
            <CollapsedLabel label={tooltipLabel}>
              <PopoverPrimitive.Trigger asChild>
                <button
                  ref={forwardedRef as React.Ref<HTMLButtonElement>}
                  type="button"
                  data-app-menu-item
                  aria-haspopup="menu"
                  aria-expanded={!!isSubMenuOpen}
                  aria-label={isTriggerItem ? tooltipLabel : undefined}
                  className={cn(
                    menuItemBase,
                    activeBg,
                    hoverBg,
                    'justify-between overflow-visible',
                    isTriggerItem ? 'px-0' : 'px-2',
                    buttonClassName
                  )}
                  onMouseEnter={() => {
                    timeoutRef.current = setTimeout(
                      () => onHover?.(),
                      onHoverDelay
                    );
                  }}
                  onMouseLeave={() => {
                    if (timeoutRef.current) clearTimeout(timeoutRef.current);
                  }}
                  onKeyDown={event => {
                    if (
                      event.key === 'ArrowRight' ||
                      event.key === 'Enter' ||
                      event.key === ' '
                    ) {
                      event.preventDefault();
                      onHover?.();
                    }
                  }}
                  onClick={onClick}
                >
                  {itemContent}
                  <Icon icon="ChevronRight" size={14} color="#191F2E" />
                </button>
              </PopoverPrimitive.Trigger>
            </CollapsedLabel>
          </div>
          {renderSubmenu}
        </PopoverPrimitive.Root>
      );
    }

    const interactiveClassName = cn(
      menuItemBase,
      activeBg,
      hoverBg,
      justify,
      'overflow-visible',
      buttonClassName
    );

    const interactive = route ? (
      <Component
        ref={forwardedRef}
        href={route}
        data-app-menu-item
        aria-current={isActive ? 'page' : undefined}
        aria-label={isTriggerItem ? tooltipLabel : undefined}
        className={interactiveClassName}
        onMouseEnter={onHover}
        onClick={(event: React.MouseEvent) => {
          if (!onClick) return;
          if (event.metaKey || event.ctrlKey || event.shiftKey) return;
          event.preventDefault();
          onClick();
        }}
      >
        {itemContent}
      </Component>
    ) : (
      <button
        ref={forwardedRef as React.Ref<HTMLButtonElement>}
        type="button"
        data-app-menu-item
        aria-current={isActive ? 'page' : undefined}
        aria-label={isTriggerItem ? tooltipLabel : undefined}
        className={interactiveClassName}
        onMouseEnter={onHover}
        onClick={onClick}
      >
        {itemContent}
      </button>
    );

    return (
      <div className={className}>
        <CollapsedLabel label={tooltipLabel}>{interactive}</CollapsedLabel>
      </div>
    );
  }
);
MenuItem.displayName = 'AppMenuItem';

type SubMenuProps = {
  title: string;
  children: React.ReactNode;
  hideTrigger?: boolean;
  renderTitle?: React.ReactNode;
  alignWithHeader?: boolean;
  className?: string;
  closeSubMenu?: () => void;
  keepOpen?: () => void;
} & React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>;

const SubMenu = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  SubMenuProps
>(
  (
    {
      title,
      children,
      hideTrigger,
      renderTitle,
      alignWithHeader,
      className,
      closeSubMenu,
      keepOpen,
      ...props
    },
    ref
  ) => (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        ref={ref}
        sideOffset={hideTrigger ? -72 : 8}
        align="start"
        alignOffset={-8}
        side={hideTrigger ? 'bottom' : 'right'}
        collisionPadding={8}
        onEscapeKeyDown={event => {
          event.preventDefault();
          closeSubMenu?.();
        }}
        onMouseEnter={() => keepOpen?.()}
        {...props}
        className={cn(
          'z-[1500] gap-0 rounded border border-[#EEEFF2]! bg-white p-0! shadow-[8px_0px_16px_0px_#191F2E3D]! outline-none motion-safe:data-[state=open]:animate-app-submenu-show motion-safe:data-[state=closed]:animate-app-submenu-hide **:data-app-menu-list:gap-1 **:[data-app-menu-item]:h-10',
          hideTrigger &&
            'fixed left-0 top-[var(--app-banner-height,0px)] rounded-none',
          className
        )}
      >
        <div
          className={cn(
            'w-full border-b border-b-[#EEEFF2]! px-4 py-3 font-[OpenSans] text-sm font-semibold text-[#191F2E]',
            alignWithHeader && 'h-[calc(72px-1px)]'
          )}
        >
          {renderTitle || title}
        </div>
        {children}
      </PopoverPrimitive.Content>
    </PopoverPrimitive.Portal>
  )
);
SubMenu.displayName = 'AppSubMenu';

export const AppMenu = Object.assign(Root, {
  Trigger,
  Bar,
  Content,
  Header,
  Footer,
  MenuIconButton,
  MenuList,
  MenuItem,
  SubMenu,
});
